import BaseStore from './BaseStore';
import _ from 'lodash';
import rp from 'request-promise';
import config from '../config';
import { getToken } from '../components/Token';
import { getTenantHeaders } from '../components/TenantHeaders';
import { clearLocalStorage } from '../utils/common';

const defaultData = {
  name: null,
  patientId: null,
  settingProfileId: null,
  getup: 0,
  walkingAt: 0,
  walkingStart: '22:00',
  walkingEnd: '04:00',
  movement: 0,
  movementLevel: 3,
  notTurnFor: 1,
  notTurnForTime: 120,
  notTurnForHours: 2,
  notTurnForMinutes: 0,
  noMovementFor: 0,
  noMovementForTime: 2880,
  noMovementForDay: 2,
  fall: 1,
  intoArea: 0,
  outOfArea: 0,
  intoAreaRID: [],
  lowBattery: 1,
  noSignal: 0,
  intoBuilding: [],
  outOfBuilding: [],
};

export class SettingProfileStore extends BaseStore {
  constructor() {
    super();
    this.observable({
      loading: false,
      data: _.cloneDeep(defaultData),
      settings: [],
      offset: 0,
      limit: 15,
      search: null,
      sort: null,
      order: null,
      action: null,
    });
  }

  setAction(task, data) {
    this[task] = data;
  }

  resetData() {
    this.data = {
      name: null,
      patientId: null,
      settingProfileId: null,
      getup: 0,
      walkingAt: 0,
      walkingStart: '22:00',
      walkingEnd: '04:00',
      movement: 0,
      movementLevel: 3,
      notTurnFor: 1,
      notTurnForTime: 120,
      notTurnForHours: 2,
      notTurnForMinutes: 0,
      noMovementFor: 0,
      noMovementForTime: 2880,
      noMovementForDay: 2,
      fall: 1,
      intoArea: 0,
      outOfArea: 0,
      intoAreaRID: [],
      lowBattery: 1,
      noSignal: 0,
      intoBuilding: [],
      outOfBuilding: [],
    };
  }

  resetQueryParams() {
    this.offset = 0;
    this.limit = 15;
    this.search = null;
    this.sort = null;
    this.order = null;
    this.settings = [];
  }

  async setData(task, data, getSettingProfile = true) {
    this[task] = data;
    if (getSettingProfile) {
      this.getSettingProfile(this.offset, this.limit, this.search, this.sort, this.order);
    }
  }

  getUTCDateTime(dateTime) {
    return dateTime.getTime() - dateTime.getTimezoneOffset() * 60 * 1000;
  }

  changeData(key, value) {
    this.data[key] = value;
  }

  async settingCreate() {
    this.loading = true;
    try {
      let detail = this.data;
      detail.userId = localStorage.getItem('user_id');
      detail.intoAreaRID = JSON.stringify(detail.intoAreaRID);
      detail.intoBuilding = JSON.stringify(detail.intoBuilding);
      detail.outOfBuilding = JSON.stringify(detail.outOfBuilding);
      const res = await rp
        .post(`${config.api.url}/${config.api.version}/setwarningprofile`, {
          body: detail,
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async settingUpdate(id) {
    this.loading = true;
    try {
      let detail = this.data;
      detail.notTurnForTime = +(+detail.notTurnForHours * 60 + +detail.notTurnForMinutes);
      detail.noMovementForTime = +detail.noMovementForDay * 1440;
      detail.getup = +detail.getup;
      detail.movement = +detail.movement;
      detail.movementLevel = +detail.movementLevel;
      detail.walkingAt = +detail.walkingAt;
      detail.notTurnFor = +detail.notTurnFor;
      detail.notTurnForTime = +detail.notTurnForTime;
      detail.notTurnForHours = +detail.notTurnForHours;
      detail.notTurnForMinutes = +detail.notTurnForMinutes;
      detail.noMovementFor = +detail.noMovementFor;
      detail.noMovementForTime = +detail.noMovementForTime;
      detail.noMovementForDay = +detail.noMovementForDay;
      detail.fall = +detail.fall;
      detail.intoArea = +detail.intoArea;
      detail.intoAreaRID = JSON.stringify(detail.intoAreaRID);
      detail.intoBuilding = JSON.stringify(detail.intoBuilding);
      detail.outOfBuilding = JSON.stringify(detail.outOfBuilding);
      detail.outOfArea = +detail.outOfArea;
      // detail.outOfArea = 1;
      detail.lowBattery = +detail.lowBattery;
      detail.noSignal = +detail.noSignal;

      const res = await rp
        .put(`${config.api.url}/${config.api.version}/setwarningprofile/${id}`, {
          body: detail,
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getSettingProfile(offset = 0, limit = 15, search = null, sort = null, order = null) {
    this.loading = true;
    let queryOption = {};
    queryOption.offset = offset;
    queryOption.limit = limit;
    if (search) {
      queryOption.search = search;
    }
    if (sort) {
      queryOption.sort = sort;
    }
    if (order) {
      queryOption.order = order;
    }
    try {
      let url = `${config.api.url}/${config.api.version}/setwarningprofile`;
      const res = await rp
        .get(url, {
          qs: queryOption,
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        let settings = res.body || {};
        this.settings = settings;
      }
    } catch (err) {
      if (err.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      }
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getSettingProfileById(id) {
    this.loading = true;
    try {
      let url = `${config.api.url}/${config.api.version}/setwarningprofile/${id}`;
      const res = await rp
        .get(url, {
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        let data = res.body;
        let settingData = '';
        if (data) {
          settingData = this.data;
          settingData.id = data.id;
          settingData.userId = data.userId;
          settingData.name = data.name;
          settingData.getup = data.getup;
          settingData.walkingAt = data.walkingAt;
          settingData.walkingStart = data.walkingStart;
          settingData.walkingEnd = data.walkingEnd;
          settingData.movement = data.movement;
          settingData.movementLevel = data.movementLevel;
          settingData.notTurnFor = data.notTurnFor;
          settingData.notTurnForTime = data.notTurnForTime;
          settingData.noMovementFor = data.noMovementFor;
          settingData.noMovementForTime = data.noMovementForTime;
          settingData.fall = data.fall;
          settingData.intoArea = data.intoArea;
          settingData.outOfArea = data.outOfArea;
          settingData.lowBattery = data.lowBattery;
          settingData.noSignal = data.noSignal;

          let hours = Math.floor(data.notTurnForTime / 60);
          let minutes = data.notTurnForTime % 60;
          let day = Math.floor(data.noMovementForTime / 1440);
          settingData.notTurnForHours = hours;
          settingData.notTurnForMinutes = minutes;
          settingData.noMovementForDay = day;
          try {
            settingData.intoAreaRID = JSON.parse(data.intoAreaRID);
            settingData.intoBuilding = JSON.stringify(data.intoBuilding);
            settingData.outOfBuilding = JSON.stringify(data.outOfBuilding);
          } catch (e) {
            settingData.intoAreaRID = '';
            settingData.intoBuilding = [];
            settingData.outOfBuilding = [];
          }
          // settingData.intoAreaRID = (data.intoAreaRID && data.intoAreaRID !== []) ? JSON.parse(data.intoAreaRID) : '';
        } else {
          settingData = defaultData;
        }
        this.data = settingData;
      }
    } catch (err) {
      if (err.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      }
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async settingProfileDelete(Id) {
    this.loading = true;
    try {
      const res = await rp
        .delete(`${config.api.url}/${config.api.version}/setwarningprofile/${Id}`, {
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async resetSettingProfile() {
    this.data = defaultData;
  }
}
export default new SettingProfileStore();
